import React from "react";
import { useApp } from "@/context/AppContext";
import { t } from "@/lib/i18n";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { LogOut, Star, Type } from "lucide-react";
import { InstallGuide } from "@/components/InstallGuide";

export default function Profile() {
  React.useEffect(() => {
    if (window.location.hash === "#install") setTimeout(() => document.getElementById("install")?.scrollIntoView({ behavior: "smooth" }), 300);
  }, []);
  const { user, lang, logout, fontSize, setFontSize } = useApp();
  if (!user) return null;
  return (
    <div className="max-w-2xl">
      <p className="uppercase tracking-[0.3em] text-xs text-[var(--text-secondary)]">FaithCycle</p>
      <h1 className="font-serif text-4xl md:text-5xl tracking-tight mt-2">
        {t("profile", lang)}
      </h1>

      <div className="mt-10 rounded-2xl border border-[var(--border)] bg-[var(--surface-elevated)] p-8 flex items-center gap-6">
        <Avatar className="w-20 h-20 border border-[var(--border)]">
          <AvatarImage src={user.picture} alt={user.name} />
          <AvatarFallback className="text-2xl bg-[var(--surface)]">
            {user.name?.[0]?.toUpperCase() || "U"}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <h2 className="font-serif text-3xl tracking-tight flex items-center gap-2" data-testid="profile-name">
            {user.name}
            {user.is_supporter && (
              <Star className="w-5 h-5 text-[var(--accent)] fill-[var(--accent)]" strokeWidth={1.5} />
            )}
          </h2>
          <p className="text-sm text-[var(--text-secondary)] mt-1" data-testid="profile-email">{user.email}</p>
          {user.is_supporter && (
            <p className="text-xs uppercase tracking-widest text-[var(--accent)] mt-2" data-testid="profile-supporter-label">
              {lang === "es" ? "Sostenedor · Acceso anticipado" : "Supporter · Early access"}
            </p>
          )}
        </div>
      </div>

      {/* Font size control — applies to Bible, Plans, Studies, Community, Groups, Bookmarks */}
      <div className="mt-6 rounded-2xl border border-[var(--border)] bg-[var(--surface-elevated)] p-6" data-testid="font-size-card">
        <div className="flex items-center gap-2 mb-1">
          <Type className="w-4 h-4 text-[var(--accent)]" strokeWidth={1.5} />
          <h3 className="font-serif text-xl tracking-tight">
            {lang === "es" ? "Tamaño de letra" : "Font size"}
          </h3>
        </div>
        <p className="text-xs text-[var(--text-secondary)] mb-5">
          {lang === "es"
            ? "Aplica a la Biblia, Planes, Estudios, Comunidad, Grupos y Marcadores."
            : "Applies to Bible, Plans, Studies, Community, Groups and Bookmarks."}
        </p>

        <div className="flex items-center gap-4">
          <button
            data-testid="font-size-decrease"
            onClick={() => setFontSize(fontSize - 1)}
            disabled={fontSize <= 10}
            className="w-10 h-10 rounded-full border border-[var(--border)] hover:border-[var(--accent)] disabled:opacity-40 flex items-center justify-center text-lg"
          >
            −
          </button>
          <input
            data-testid="font-size-slider"
            type="range"
            min="10"
            max="18"
            step="1"
            value={fontSize}
            onChange={(e) => setFontSize(e.target.value)}
            className="flex-1 accent-[var(--accent)]"
          />
          <button
            data-testid="font-size-increase"
            onClick={() => setFontSize(fontSize + 1)}
            disabled={fontSize >= 18}
            className="w-10 h-10 rounded-full border border-[var(--border)] hover:border-[var(--accent)] disabled:opacity-40 flex items-center justify-center text-lg"
          >
            +
          </button>
          <div className="min-w-[70px] text-center">
            <span className="font-serif text-2xl" data-testid="font-size-value">{fontSize}</span>
            <span className="text-xs text-[var(--text-secondary)] ml-1">pt</span>
          </div>
        </div>

        <div className="mt-5 pt-5 border-t border-[var(--border)]">
          <p className="text-[10px] uppercase tracking-widest text-[var(--text-secondary)] mb-2">
            {lang === "es" ? "Vista previa" : "Preview"}
          </p>
          <p className="font-serif-body leading-relaxed" style={{ fontSize: `${fontSize + 2}px` }}>
            "Porque de tal manera amó Dios al mundo, que dio a su Hijo unigénito."
          </p>
          <p className="mt-2" style={{ fontSize: `${fontSize}px` }}>
            {lang === "es"
              ? "Comentario, meditación y contenido general se ajustan a este tamaño."
              : "Commentary, meditation and body text adjust to this size."}
          </p>
        </div>
      </div>

      <InstallGuide />

      <div className="mt-8 flex flex-wrap gap-3">
        {user?.is_admin && (
          <Button
            data-testid="profile-admin-btn"
            onClick={() => (window.location.href = "/admin")}
            className="rounded-full h-11 px-5 bg-[var(--accent)] text-white hover:bg-[var(--accent)]/90"
          >
            {lang === "es" ? "Panel del dueño" : "Owner panel"}
          </Button>
        )}
        <Button
          data-testid="profile-signout-btn"
          onClick={logout}
          variant="outline"
          className="rounded-full h-11 px-5 border-[var(--border)]"
        >
          <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
          {t("signOut", lang)}
        </Button>
      </div>
    </div>
  );
}
